import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field, GoldButton, GhostButton, NativeSelect } from "@/components/atlas/Fields";
import { PositionSelect } from "@/components/events/PositionsEditor";
import { api } from "@/lib/api";
import { usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";

const BLANK = { name: "", email: "", phone: "", role: "employee", primary_position: "server", positions: [], status: "active", preferred_areas: [], notes: "", lang: "fr", password: "" };
const CATS = ["hospitality", "kitchen", "operations", "custom"];

export const EmployeeDialog = ({ open, onClose, employee, onSaved }) => {
  const { t } = useLang();
  const { positions, label } = usePositions();
  const [f, setF] = useState(BLANK);
  const [areas, setAreas] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = employee ? { ...BLANK, ...employee, password: "" } : BLANK;
    setF(base);
    setAreas((base.preferred_areas || []).join(", "));
  }, [open, employee]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const toggle = (key) => setF((p) => ({ ...p, positions: p.positions.includes(key) ? p.positions.filter((x) => x !== key) : [...p.positions, key] }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, role: "employee", preferred_areas: areas.split(",").map((s) => s.trim()).filter(Boolean), password: null };
      const r = employee ? await api.put(`/employees/${employee.id}`, payload) : await api.post("/employees", payload);
      toast.success(employee ? t("emp.updated") : t("emp.created"));
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl">{employee ? t("emp.edit") : t("emp.new")}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label={t("c.name")}><Input data-testid="employee-name-input" value={f.name} onChange={set("name")} className="h-10 rounded-none" /></Field>
          <Field label={t("c.email")}><Input data-testid="employee-email-input" type="email" value={f.email} onChange={set("email")} className="h-10 rounded-none" /></Field>
          <Field label={t("c.phone")}><Input data-testid="employee-phone-input" value={f.phone} onChange={set("phone")} className="h-10 rounded-none" /></Field>
          <Field label={t("emp.primary")}><PositionSelect value={f.primary_position} onChange={set("primary_position")} testid="employee-primary-select" allowEmpty={!f.primary_position} /></Field>
          <Field label={t("emp.lang")}>
            <NativeSelect testid="employee-lang-select" value={f.lang} onChange={set("lang")}><option value="fr">Français</option><option value="en">English</option></NativeSelect>
          </Field>
          <Field label={t("emp.qualified")} className="md:col-span-2">
            <div className="space-y-3 border border-black/[0.08] p-4">
              {CATS.map((c) => {
                const list = positions.filter((p) => p.category === c);
                if (!list.length) return null;
                return (
                  <div key={c}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d2f] mb-2">{t(`cat.${c}`)}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {list.map((p) => (
                        <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox data-testid={`employee-qual-${p.key}`} checked={f.positions.includes(p.key) || f.primary_position === p.key} disabled={f.primary_position === p.key} onCheckedChange={() => toggle(p.key)} />
                          {label(p.key)}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Field>
          <Field label={t("emp.areas")} className="md:col-span-2"><Input data-testid="employee-areas-input" value={areas} onChange={(e) => setAreas(e.target.value)} className="h-10 rounded-none" /></Field>
          <Field label={t("emp.notes")} className="md:col-span-2"><Textarea data-testid="employee-notes-input" value={f.notes} onChange={set("notes")} rows={2} className="rounded-none" /></Field>
          {f.role !== "admin" && <label className="flex items-center gap-3 text-sm">
            <Switch data-testid="employee-status-switch" checked={f.status === "active"} onCheckedChange={(v) => set("status")(v ? "active" : "inactive")} />
            {f.status === "active" ? t("emp.active") : t("emp.inactive")}
          </label>}
        </div>
        <DialogFooter>
          <GhostButton onClick={onClose} data-testid="employee-dialog-cancel">{t("c.cancel")}</GhostButton>
          <GoldButton onClick={save} disabled={busy || !f.name || !f.email} data-testid="employee-dialog-save">{t("c.save")}</GoldButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
